<div class="tw-flex tw-w-full tw-h-full tw-bg-color-surface-page">
  <!-- One announcement for the active view's loading, rather than one per skeleton (#6305). -->
  <app-loading-announcer
    [loading]="showFilterSkeleton()"
    [loadingKey]="viewLoadingKeys().loading"
    [loadedKey]="viewLoadingKeys().loaded"
  />
  <!-- min-sm:min-w-min: at 640–900px the tabs and content rows can't shrink, so grow with them to keep
       header/filter/content one width. Not on phones: it would stop filter pills and large amounts shrinking.
       No side padding of its own: user-layout's <main> is the one gutter for every page (#6171). -->
  <div class="tw-flex tw-flex-col tw-w-full min-sm:tw-min-w-min tw-h-full tw-relative">
    <!-- Sticky header wrapper - stays at top while respecting layout. Its height never changes
         (#6050): the filter hides by sliding up under the tabs/search row with transform + opacity
         only, so nothing below it reflows or jumps while the page scrolls. The wrapper is
         click-through so the empty slot a hidden filter leaves doesn't swallow taps and scrolls
         meant for the content under it; the tabs/search row and the filter opt back in. -->
    <div
      class="tw-sticky tw-top-0 tw-z-50 tw-pointer-events-none"
      (focusin)="onHeaderFocusIn($event)"
      (focusout)="onHeaderFocusOut($event)"
    >
      <!-- tabs + search. Owns the page background and, once the filter is collapsed, the rounded
           shadow look; tw-z-10 keeps it above the filter as that slides in and out behind it. -->
      <div
        #headerShell
        data-testid="views-header-shell"
        class="tw-relative tw-z-10 tw-pointer-events-auto tw-pt-2 tw-bg-color-surface-page tw-transition-[border-radius,box-shadow] tw-duration-300"
        [ngClass]="hideFilter() ? 'tw-rounded-xl tw-shadow-button tw-overflow-hidden' : ''"
      >
        <div
          class="tw-flex tw-justify-between sm:tw-relative tw-w-full"
          [ngClass]="{ 'sm:tw-overflow-hidden': !isSearchFocused }"
        >
          <!--    view switch tabs -->
          <div
            class="tw-flex tw-items-end tw-gap-1"
            [ngClass]="isSearchFocused ? 'sm:tw-hidden' : ''"
          >
            <!-- xs:w-auto xs:min-w-0: below 475px, size each tab to its label. md:min-w-[106px] (and
               sm:w-[30%]) otherwise keep all three ~106px+, which with the 16px phone gutter leaves the
               search icon on top of the Future tab. (No ! needed: the max-width screens are listed
               largest first, so xs: comes last in the CSS and wins over md:/sm:, #6170.) -->
            @for (button of viewTabs; track button; let idx = $index) {
              @let isActive = button.value === viewsService.view;
              <!-- A real button (was a clickable <div>) so the view switch is reachable with Tab and
                 works with Enter/Space; aria-pressed marks the current view. Its focus indicator is
                 the span at the end: a top/left/right frame, open at the bottom (an outline can't
                 skip a side), the same on every tab: it sits 2px out, on the active tab's edge like
                 the flared bottom corners' traced curve, and stops 12px up, where those corners start,
                 so views.component.scss can carry it round them without a seam (an inactive tab gets
                 the curves alone, without the active tab's fill). -->
              <button
                type="button"
                [attr.aria-pressed]="isActive"
                [attr.data-testid]="button.value.toLowerCase() + '-view-button'"
                class="tw-relative tw-flex tw-flex-col tw-items-center tw-gap-1 tw-px-8 tw-py-3 tw-text-xl tw-font-medium sm:tw-px-4 sm:tw-py-1 xs:tw-w-auto xs:tw-min-w-0 sm:tw-text-lg tw-capitalize tw-min-w-[180px] md:tw-min-w-[106px] sm:tw-w-[30%] tw-cursor-pointer tw-group focus-visible:tw-outline-none view-tab
          {{
                  isActive
                    ? 'tab-active tw-rounded-tl-3xl tw-rounded-tr-3xl sm:tw-rounded-tl-xl sm:tw-rounded-tr-xl'
                    : 'tw-bg-transparent'
                }}"
                [ngClass]="{
                  'view-tab-first': idx === 0,
                  'tab-active-first': isActive && idx === 0,
                  'tab-active-middle': isActive && idx === 1,
                  'tab-active-last': isActive && idx === 2,
                }"
                [ngStyle]="{ '--tab-bg-color': button.borderColorVar }"
                (click)="toggleTimeButtons(button.value)"
              >
                <span class="tw-flex tw-gap-2 tw-justify-center tw-items-center">
                  @if (button.value === ViewEnum.TODAY) {
                    <icon
                      [name]="'home'"
                      [size]="6"
                      [currentColor]="'dt-text-today-primary'"
                    ></icon>
                  }
                  {{ button.value }}
                </span>

                <span
                  class="tw-block tw-w-full tw-h-1 tw-rounded-tl tw-rounded-tr {{
                    isActive ? button.underlineActive : button.underlineInactive
                  }}"
                ></span>
                <span
                  aria-hidden="true"
                  data-testid="view-tab-focus-frame"
                  class="tw-pointer-events-none tw-absolute tw-top-0 tw-z-10 tw-hidden tw-border-2 tw-border-b-0 tw-border-solid tw-border-color-action-primary -tw-inset-x-0.5 tw-bottom-3 tw-rounded-tl-3xl tw-rounded-tr-3xl sm:tw-rounded-tl-xl sm:tw-rounded-tr-xl group-focus-visible:tw-block"
                ></span>
              </button>
            }
          </div>

          <!--    search-->
          <app-search-bar
            class="tw-w-1/2 sm:tw-w-full tw-mb-1"
            [transactions]="pastTransactionTable.source"
            (transactionSelected)="openTransactionDialog($event)"
            (isSearchFocused)="isSearchFocused = $event"
          ></app-search-bar>
        </div>
      </div>

      <!--      filter section - connects to tabs -->
      <!-- The outer div is the slide: it keeps its full height (the filter's spot in the page) and
           only moves and fades. It has no transform while shown, so nothing inside it gets a new
           containing block for fixed-position panels. It also backs the filter's rounded corners
           with the page colour, which the header background used to do. inert while hidden: it is
           only moved out of sight, so without it Tab still lands on its controls, Enter opens a
           datepicker behind the tabs and screen readers keep reading the filter. -->
      <div
        data-testid="views-filter-slide"
        class="tw-pointer-events-auto tw-bg-color-surface-page tw-transition-[transform,opacity] tw-duration-300 tw-ease-in-out"
        [ngClass]="hideFilter() ? '-tw-translate-y-full tw-opacity-0' : 'tw-opacity-100'"
      >
        <div
          #filterSection
          class="tw-gap-3 tw-flex tw-flex-col tw-p-3 xs:tw-p-2"
          [attr.inert]="hideFilter() || null"
          [ngClass]="{
            'filter-connect-first': viewsService.view === ViewEnum.PAST,
            'filter-connect-last': viewsService.view === ViewEnum.FUTURE,
            'filter-connect-middle': viewsService.view === ViewEnum.TODAY,
          }"
          [class]="activeView?.buttonBorderActive + ' ' + activeView?.filter"
        >
          <!-- Only the filter controls reveal on tab switch: this container's background joins the active tab -->
          <div #viewFilter data-testid="views-filter-controls" class="tw-flex tw-flex-col tw-gap-3">
            @if (showFilterSkeleton()) {
              <!-- Skeleton mirrors app-views-control's wrapper + label/datepicker + filter-button
                 layout 1:1 (same classes) so placeholder widths track the real control at every breakpoint -->
              <div class="tw-w-full tw-min-w-[296px]" aria-hidden="true">
                <div
                  class="tw-flex tw-w-full tw-h-full tw-justify-between tw-items-center tw-p-2 tw-gap-4 xl:tw-flex-col tb:tw-gap-2 tb:tw-rounded-3xl xxs:tw-p-0"
                >
                  <div class="tw-flex tw-w-full tw-h-full tw-gap-2 tw-items-center">
                    <div
                      class="tw-h-12 tw-rounded-2xl tw-bg-dt-slate-8/40 tw-animate-pulse tw-shrink-0 {{
                        viewsService.view === ViewEnum.TODAY
                          ? 'tw-min-w-28 tw-w-fit'
                          : 'tw-w-40 sm:tw-w-20'
                      }}"
                    ></div>
                    <div
                      class="tw-flex-1 tw-h-12 tw-rounded-2xl tw-bg-dt-slate-8/40 tw-animate-pulse"
                    ></div>
                  </div>
                  <div class="tw-w-full tw-h-12">
                    <div
                      class="tw-w-full tw-h-full tw-rounded-2xl tw-bg-dt-slate-8/40 tw-animate-pulse"
                    ></div>
                  </div>
                </div>
              </div>
            } @else {
              <app-views-control
                [currentView]="viewsService.view"
                (granularityPicked)="pastTransactionTable.replayIntro()"
              ></app-views-control>
            }
          </div>
        </div>
      </div>
    </div>
    <!-- End sticky header -->

    <!--      views content -->
    <div
      #viewContent
      data-testid="views-content"
      class="tw-flex tw-w-full tw-h-full tw-gap-4 sm:tw-flex-col tw-py-3"
      (keydown)="onContentKeydown($event)"
    >
      <div class="tw-flex tw-w-full tw-flex-col tw-gap-4">
        @switch (viewsService.view) {
          @case (ViewEnum.TODAY) {
            <app-current-balance></app-current-balance>
          }
        }
        <app-group-by-chart-control [currentView]="viewsService.view"></app-group-by-chart-control>
      </div>
      <div class="tw-w-full">
        @switch (viewsService.view) {
          @case (ViewEnum.PAST) {
            <app-widget
              [title]="'transactions' | i18n"
              [iconName]="'transactions'"
              class="transaction-detail"
            >
              @if (isPastViewReady()) {
                <!-- inert for the whole intro: while the list builds hidden its rows can't be clicked or read out
                     before they show, and while the months collapse the intro is still steering the grid, so a
                     click on a month would fight it. The page still scrolls (the grid is autoHeight). -->
                <div class="tw-relative" [attr.inert]="pastTransactionTable.introActive() || null">
                  <app-ag-grid-table
                    [options]="pastTransactionTable?.options"
                    [data]="pastTransactionTable?.gridData()"
                    [customGridClass]="pastTransactionTable?.motionClass()"
                  />
                  <!-- The list builds hidden (see past-group-intro.motion.ts): the skeleton stays over it
                       until it is ready, then fades out as the list fades in. It fills the wrapper and
                       clips, so on a short list it can't hang below the card. -->
                  @if (pastTransactionTable.introActive()) {
                    <div
                      class="past-intro-skeleton tw-absolute tw-inset-0 tw-overflow-hidden tw-pointer-events-none"
                      [class.past-intro-skeleton--out]="!pastTransactionTable.introPending()"
                      aria-hidden="true"
                    >
                      <ng-container [ngTemplateOutlet]="pastSkeleton" />
                    </div>
                  }
                </div>
              } @else if (showPastSkeleton()) {
                <ng-container [ngTemplateOutlet]="pastSkeleton" />
              }
              <ng-template #pastSkeleton>
                <!-- Skeleton mirrors past-group-header — keep in sync -->
                <div class="tw-flex tw-flex-col tw-w-full">
                  @for (_ of [1, 2, 3, 4, 5, 6, 7, 8]; track $index) {
                    <div
                      class="tw-flex tw-items-center tw-gap-3 tw-px-4 tw-py-3 border-bottom-gray"
                    >
                      <div
                        class="tw-h-4 tw-w-24 tw-rounded tw-bg-dt-slate-8/40 tw-animate-pulse"
                      ></div>
                      <div
                        class="tw-flex-1 tw-h-4 tw-rounded tw-bg-dt-slate-8/40 tw-animate-pulse"
                      ></div>
                      <div
                        class="tw-h-4 tw-w-20 tw-rounded tw-bg-dt-slate-8/40 tw-animate-pulse"
                      ></div>
                      <div
                        class="tw-h-4 tw-w-20 tw-rounded tw-bg-dt-slate-8/40 tw-animate-pulse"
                      ></div>
                    </div>
                  }
                </div>
              </ng-template>
            </app-widget>
          }
          @case (ViewEnum.TODAY) {
            <app-widget
              [title]="'accounts' | i18n"
              [iconName]="'wallet'"
              [backgroundClass]="'tw-bg-glass-panel tw-backdrop-blur-[20px]'"
            >
              @if (syncProcessIndicator(); as syncProcess) {
                <div
                  class="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-text-xs tw-text-dt-text-secondary"
                >
                  <span
                    class="tw-inline-block tw-w-3 tw-h-3 tw-rounded-full tw-border-2 tw-border-primary tw-animate-spin"
                    style="border-top-color: transparent"
                  ></span>
                  <span>{{ syncProcess.message }}</span>
                </div>
              }
              @if (isTodayViewReady()) {
                <app-ag-grid-table
                  [options]="accountsViewTable?.options"
                  [data]="accountsViewTable.data()"
                  [customGridClass]="'remove-row-borders today-accounts-grid'"
                />
              } @else if (showTodaySkeleton()) {
                <app-account-row-skeleton
                  cardClass="tw-flex tw-w-full tw-items-center tw-gap-3 tw-p-4 tw-shadow-elevation-card tw-rounded-2xl tw-border today-account-card-inner"
                  [showChevron]="true"
                />
              }
            </app-widget>
          }
          @case (ViewEnum.FUTURE) {
            <app-widget
              [title]="'transactions' | i18n"
              [iconName]="'transactions'"
              class="future-table"
            >
              <div class="tw-flex tw-flex-col tw-gap-6">
                <div class="">
                  <app-path-tabs
                    [options]="getTabOptions()"
                    [finalBalanceAmountList]="scenarioTransactionTable?.finalBalanceAmountList"
                    [defaultTabSelected]="scenarioStore.currentScenarioType()"
                    (onChange)="setScenarioPath($event)"
                    (isExpanded)="receiveIsExpanded($event)"
                  ></app-path-tabs>
                  @if (isTabExpanded) {
                    <app-future-table-tabs-info [scenarioDetails]="scenarioSelected()">
                    </app-future-table-tabs-info>
                  }
                </div>
                @if (isFutureViewReady()) {
                  <app-ag-grid-table
                    [options]="scenarioTransactionTable?.options"
                    [data]="scenarioTransactionTable?.data()"
                    [hasCursorPointer]="viewsService.view !== ViewEnum.FUTURE"
                  />
                } @else if (showFutureSkeleton()) {
                  <div class="tw-flex tw-flex-col tw-w-full">
                    @for (_ of [1, 2, 3, 4, 5, 6]; track $index) {
                      <div
                        class="tw-flex tw-items-center tw-gap-3 tw-px-4 tw-py-3 border-bottom-gray"
                      >
                        <div
                          class="tw-h-4 tw-w-24 tw-rounded tw-bg-dt-slate-8/40 tw-animate-pulse"
                        ></div>
                        <div
                          class="tw-flex-1 tw-h-4 tw-w-32 tw-rounded tw-bg-dt-slate-8/40 tw-animate-pulse"
                        ></div>
                        <div
                          class="tw-h-4 tw-w-20 tw-rounded tw-bg-dt-slate-8/40 tw-animate-pulse"
                        ></div>
                        <div
                          class="tw-h-4 tw-w-20 tw-rounded tw-bg-dt-slate-8/40 tw-animate-pulse"
                        ></div>
                      </div>
                    }
                  </div>
                }
              </div>
            </app-widget>
          }
        }
      </div>
    </div>
  </div>
</div>
